/**
 * Public site footer — neo-brutalist four-column block.
 *
 * Every link here points at a route that actually exists and is reachable
 * without logging in, except the last one, which is the deliberate way into
 * the dashboard.
 *
 * @module layouts/Footer
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  APPLICATION_MAP_DIRECTIONS_URL,
  APPLICATION_MAP_EMBED_SRC,
  CONTACT_EMAIL,
  FACEBOOK_URL,
  getContactPhoneDisplayLabel,
  getContactTelHref,
} from '../shared/utils/contactInfo';
import './Footer.scss';

type FooterLink = { to: string; label: string };

const STUDENT_LINKS: FooterLink[] = [
  { to: '/dong-phi-thi-lai', label: 'Tra cứu điểm & đóng phí thi lại' },
  { to: '/danh-sach-hoan-tien', label: 'Danh sách hoàn lệ phí' },
  { to: '/danh-sach-thi', label: 'Danh sách dự sát hạch' },
  { to: '/tra-cuu-buu-pham', label: 'Tra cứu bưu phẩm GPLX' },
  { to: '/review', label: 'Ôn tập lý thuyết' },
  { to: '/teststudent', label: 'Thi thử trực tuyến' },
];

const SUPPORT_LINKS: FooterLink[] = [
  { to: '/tin-tuc', label: 'Bảng tin & thông báo' },
  { to: '/tools', label: 'Bộ công cụ miễn phí' },
  // Required published notice under Law 91/2025/QH15. The footer is where a
  // person looks for it, and it has to be reachable from every public page.
  { to: '/chinh-sach-du-lieu', label: 'Chính sách bảo vệ dữ liệu cá nhân' },
  { to: '/dashboard', label: 'Đăng nhập hệ thống' },
];

export const Footer: React.FC = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="nb-footer">
      <div className="nb-footer__inner">
        <div className="nb-footer__col nb-footer__col--wide">
          <div className="nb-footer__brand">
            <img src="/assets/images/logo.png" alt="" className="nb-footer__logo" />
            <span className="nb-footer__wordmark">LÁI XE TRUNG BỘ</span>
          </div>
          <p className="nb-footer__desc">
            Trung tâm đào tạo &amp; sát hạch lái xe với phương châm “Học vui — Lái chuẩn — Thi đậu”.
          </p>
          <a className="nb-footer__contact" href={getContactTelHref()}>
            <span className="nb-icon nb-icon--18 nb-icon--fill" aria-hidden="true">call</span>
            {getContactPhoneDisplayLabel()}
          </a>
          <a className="nb-footer__contact" href={`mailto:${CONTACT_EMAIL}`}>
            <span className="nb-icon nb-icon--18" aria-hidden="true">mail</span>
            {CONTACT_EMAIL}
          </a>
          {/* Link ra ngoài: rel="noopener" để trang đích không chạm được vào
              window.opener của mình. */}
          <a
            className="nb-footer__contact"
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="nb-icon nb-icon--18 nb-icon--fill" aria-hidden="true">thumb_up</span>
            Trang Facebook của trung tâm
          </a>
          <p className="nb-footer__copy">© {year} Lái Xe Trung Bộ. Mọi quyền được bảo lưu.</p>
        </div>

        <div className="nb-footer__col">
          <h2 className="nb-footer__heading">Dành cho học viên</h2>
          <ul className="nb-footer__list">
            {STUDENT_LINKS.map((link) => (
              <li key={link.to}>
                <Link to={link.to} className="nb-footer__link">{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="nb-footer__col">
          <h2 className="nb-footer__heading">Hỗ trợ</h2>
          <ul className="nb-footer__list">
            {SUPPORT_LINKS.map((link) => (
              <li key={link.to}>
                <Link to={link.to} className="nb-footer__link">{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="nb-footer__col">
          <h2 className="nb-footer__heading">Điểm tiếp nhận hồ sơ</h2>
          <div className="nb-footer__map">
            <iframe
              src={APPLICATION_MAP_EMBED_SRC}
              title="Bản đồ điểm nộp hồ sơ"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <a
            className="nb-footer__link nb-footer__link--directions"
            href={APPLICATION_MAP_DIRECTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="nb-icon nb-icon--18" aria-hidden="true">directions</span>
            Chỉ đường trên Google Maps
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
